import React, { useState, useEffect } from 'react';

const C = {
  blue:     '#1a56db',
  blueDark: '#1446b8',
  white:    '#ffffff',
  bg:       '#f0f2f5',
  gray2:    '#f3f4f6',
  gray3:    '#e5e7eb',
  gray4:    '#9ca3af',
  gray5:    '#6b7280',
  gray6:    '#374151',
  gray7:    '#111827',
  green:    '#16a34a',
  greenBg:  '#dcfce7',
  red:      '#dc2626',
  redBg:    '#fee2e2',
  amber:    '#d97706',
  amberBg:  '#fef3c7',
};

const font = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

const STEPS = [
  'Connect your NanoC6 to this computer via USB',
  'Click Update Beacon below',
  'Select the serial port from the popup (usually the only option)',
  'Wait for the update to complete — the beacon will restart automatically',
];

function LogLine({ text, type }) {
  const color = type === 'ok' ? C.green : type === 'err' ? C.red : type === 'info' ? C.blue : C.gray5;
  return (
    <div style={{ fontSize: 12, fontFamily: 'monospace', color, lineHeight: 1.6, wordBreak: 'break-all' }}>
      {text}
    </div>
  );
}

export default function UpdatePage() {
  const [supported,   setSupported]   = useState(true);
  const [firmwareInfo, setFirmwareInfo] = useState(null);
  const [status,      setStatus]      = useState('');
  const [progress,    setProgress]    = useState(0);
  const [logs,        setLogs]        = useState([]);
  const [running,     setRunning]     = useState(false);
  const [result,      setResult]      = useState(null); // 'success' | 'error' | null

  useEffect(() => {
    if (!('serial' in navigator)) { setSupported(false); return; }
    // Fetch firmware info
    fetch('/api/firmware/info')
      .then(r => r.json())
      .then(d => setFirmwareInfo(d))
      .catch(() => {});
  }, []);

  function addLog(text, type = '') {
    setLogs(prev => [...prev, { text, type }]);
  }

  async function sleep(ms) {
    return new Promise(r => setTimeout(r, ms));
  }

  async function readUntil(reader, waitFor, timeoutMs = 5000) {
    const dec = new TextDecoder();
    let buf = '';
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const { value, done } = await Promise.race([
        reader.read(),
        new Promise(res => setTimeout(() => res({ value: null, done: false }), 100)),
      ]);
      if (done) break;
      if (value) {
        buf += dec.decode(value);
        if (buf.includes(waitFor)) return buf;
      }
    }
    return buf;
  }

  async function startUpdate() {
    setRunning(true);
    setLogs([]);
    setProgress(0);
    setResult(null);
    setStatus('');

    let port;
    try {
      // Fetch firmware from server
      setStatus('Fetching latest firmware…');
      addLog('Downloading firmware from server…', 'info');
      const resp = await fetch('/api/firmware/main.py');
      if (!resp.ok) throw new Error('Failed to fetch firmware from server');
      const firmware = await resp.text();
      addLog(`Firmware downloaded (${firmware.length} bytes)`, 'ok');
      setProgress(10);

      // Request serial port
      setStatus('Requesting serial port…');
      addLog('Waiting for port selection…', 'info');
      port = await navigator.serial.requestPort();
      await port.open({ baudRate: 115200 });
      addLog('Serial port opened at 115200 baud', 'ok');
      setProgress(20);

      const writer = port.writable.getWriter();
      const reader = port.readable.getReader();

      // Enter raw REPL
      setStatus('Connecting to MicroPython…');
      addLog('Interrupting running script…', 'info');
      await writer.write(new Uint8Array([0x03])); await sleep(300);
      await writer.write(new Uint8Array([0x03])); await sleep(300);
      await writer.write(new Uint8Array([0x01])); // Ctrl+A = raw REPL
      const rawResp = await readUntil(reader, 'raw REPL', 4000);
      if (!rawResp.includes('raw REPL')) throw new Error('Could not enter raw REPL. Is UIFlow firmware installed?');
      addLog('Raw REPL mode active', 'ok');
      setProgress(30);

      // Encode firmware as base64 and write using binascii
      setStatus('Uploading firmware…');
      addLog('Encoding firmware for transfer…', 'info');

      const enc = new TextEncoder();
      // Convert firmware string to base64
      const b64 = btoa(unescape(encodeURIComponent(firmware)));
      addLog(`Base64 encoded: ${b64.length} chars`, 'info');
      setProgress(35);

      // Write decode+save script in chunks via raw REPL
      const CHUNK = 60; // safe base64 chunk size for raw REPL
      const chunks = [];
      for (let i = 0; i < b64.length; i += CHUNK) {
        chunks.push(b64.slice(i, i + CHUNK));
      }

      // Init: open file and import binascii
      const initCmd = `import binascii\nf=open('main.py','wb')\n`;
      await writer.write(enc.encode(initCmd));
      await writer.write(new Uint8Array([0x04]));
      await readUntil(reader, '>', 3000);
      setProgress(40);

      // Write each chunk
      for (let i = 0; i < chunks.length; i++) {
        const cmd = `f.write(binascii.a2b_base64('${chunks[i]}'))\n`;
        await writer.write(enc.encode(cmd));
        await writer.write(new Uint8Array([0x04]));
        await readUntil(reader, '>', 2000);
        const pct = 40 + Math.round(((i + 1) / chunks.length) * 50);
        setProgress(pct);
        setStatus(`Uploading firmware… ${Math.round(((i + 1) / chunks.length) * 100)}%`);
      }

      // Close file
      await writer.write(enc.encode(`f.close()\n`));
      await writer.write(new Uint8Array([0x04]));
      await readUntil(reader, '>', 2000);
      addLog('File written successfully', 'ok');
      setProgress(92);

      // Restart using machine.reset() via raw REPL
      setStatus('Restarting beacon…');
      addLog('Sending machine.reset()…', 'info');
      // Exit raw REPL first (Ctrl+B), then send reset command
      await writer.write(new Uint8Array([0x02])); // Ctrl+B = normal REPL
      await sleep(500);
      await writer.write(enc.encode('import machine; machine.reset()\r\n'));
      await sleep(1500);
      addLog('Beacon restarting…', 'ok');
      setProgress(100);
      setStatus('Update complete');
      setResult('success');

      reader.releaseLock();
      writer.releaseLock();

    } catch (err) {
      addLog('Error: ' + err.message, 'err');
      setStatus('Update failed');
      setResult('error');
    } finally {
      if (port) { try { await port.close(); } catch (e) {} }
      setRunning(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh', background: C.bg,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '40px 20px', fontFamily: font,
    }}>
      <div style={{
        background: C.white, borderRadius: 16, padding: '36px 32px',
        maxWidth: 520, width: '100%',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            MessageBeacon
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.gray7, margin: 0, marginBottom: 8 }}>
            Firmware Updater
          </h1>
          <p style={{ fontSize: 14, color: C.gray5, margin: 0, lineHeight: 1.6 }}>
            Push the latest beacon firmware to a connected NanoC6 over USB.
          </p>
        </div>

        {/* Firmware info */}
        {firmwareInfo && (
          <div style={{ background: C.gray2, borderRadius: 10, padding: '10px 14px', marginBottom: 24, fontSize: 12, color: C.gray5, display: 'flex', gap: 16 }}>
            <span>Version: <strong style={{ color: C.gray7 }}>{firmwareInfo.version}</strong></span>
            <span>Size: <strong style={{ color: C.gray7 }}>{(firmwareInfo.size / 1024).toFixed(1)} KB</strong></span>
            <span>Updated: <strong style={{ color: C.gray7 }}>{new Date(firmwareInfo.updated).toLocaleDateString('en-GB')}</strong></span>
          </div>
        )}

        {/* Browser warning */}
        {!supported && (
          <div style={{ background: C.amberBg, border: `1px solid ${C.amber}`, borderRadius: 10, padding: '12px 14px', marginBottom: 24, fontSize: 13, color: C.amber, lineHeight: 1.5 }}>
            ⚠ Web Serial is only supported in <strong>Chrome</strong> or <strong>Edge</strong>. Please open this page in one of those browsers.
          </div>
        )}

        {/* Steps */}
        <ol style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {STEPS.map((step, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%', background: C.blue,
                color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1,
              }}>{i + 1}</div>
              <span style={{ fontSize: 14, color: C.gray6, lineHeight: '24px' }}>{step}</span>
            </li>
          ))}
        </ol>

        {/* Button */}
        <button
          onClick={startUpdate}
          disabled={!supported || running}
          style={{
            width: '100%', padding: '14px', border: 'none', borderRadius: 10,
            background: (!supported || running) ? C.gray3 : C.blue,
            color: (!supported || running) ? C.gray4 : C.white,
            fontSize: 15, fontWeight: 600, cursor: (!supported || running) ? 'not-allowed' : 'pointer',
            fontFamily: font, transition: 'background 0.15s',
          }}
          onMouseEnter={e => { if (!running && supported) e.currentTarget.style.background = C.blueDark; }}
          onMouseLeave={e => { if (!running && supported) e.currentTarget.style.background = C.blue; }}
        >
          {running ? 'Updating…' : 'Update Beacon'}
        </button>

        {/* Progress + log */}
        {(running || result) && (
          <div style={{ marginTop: 20 }}>
            {/* Status */}
            <div style={{ fontSize: 13, color: C.gray5, marginBottom: 8 }}>{status}</div>

            {/* Progress bar */}
            <div style={{ height: 4, background: C.gray3, borderRadius: 99, marginBottom: 12, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: C.blue, borderRadius: 99, width: `${progress}%`, transition: 'width 0.3s ease' }} />
            </div>

            {/* Log */}
            <div style={{ background: C.gray2, borderRadius: 10, padding: '12px 14px', maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {logs.map((l, i) => <LogLine key={i} text={l.text} type={l.type} />)}
            </div>

            {/* Result */}
            {result && (
              <div style={{
                marginTop: 12, padding: '12px 14px', borderRadius: 10, fontSize: 14, fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 10,
                background: result === 'success' ? C.greenBg : C.redBg,
                color: result === 'success' ? C.green : C.red,
                border: `1px solid ${result === 'success' ? C.green : C.red}`,
              }}>
                <span style={{ fontSize: 18 }}>{result === 'success' ? '✓' : '✕'}</span>
                {result === 'success'
                  ? 'Firmware updated successfully. Beacon is restarting.'
                  : 'Update failed. Check the log above for details.'}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.gray3}`, fontSize: 12, color: C.gray4, textAlign: 'center' }}>
          Chrome / Edge only · Web Serial API
        </div>
      </div>
    </div>
  );
}
